import { Button, Input, Select, SelectItem } from '@heroui/react'
import { type UserDataResponse, useTransferPrimaryAdmin } from '@qse/api'
import type { ModalComponentProps } from '@qse/constants'
import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface TransferAdminData {
  targetUser: UserDataResponse
  availableAdmins: UserDataResponse[]
}

export default function TransferAdminModal(props: ModalComponentProps) {
  const { data, onClose } = props
  const transferData = data as { data: TransferAdminData }
  const { availableAdmins } = transferData.data

  const [selectedAdminId, setSelectedAdminId] = useState<string>('')
  const [confirmationText, setConfirmationText] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { mutateAsync: transferPrimaryAdmin } = useTransferPrimaryAdmin()

  const isConfirmationValid = confirmationText.toUpperCase() === 'TRANSFER'
  const isFormValid = selectedAdminId && isConfirmationValid

  const handleTransfer = async () => {
    if (!isFormValid) return

    setIsLoading(true)
    try {
      await transferPrimaryAdmin({ newAdminId: selectedAdminId })
      toast.success('Primary admin role transferred successfully')
      onClose()
    } catch (error: any) {
      console.error('Failed to transfer primary admin:', error)
      toast.error(error?.message || 'Failed to transfer primary admin role')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="p-6">
      <div className="flex flex-col items-center text-center mb-6">
        <div className="flex-shrink-0 w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Transfer Primary Admin Role
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            This will transfer the primary admin role to the selected user. Their contact
            information will become the tenant contact info.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select New Primary Admin:
          </label>
          <Select
            placeholder="Choose a tenant admin"
            selectedKeys={selectedAdminId ? [selectedAdminId] : []}
            classNames={{
              trigger: 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600',
              value: 'text-gray-900 dark:text-white',
              popoverContent:
                'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600',
            }}
            renderValue={items => {
              if (items.length === 0) return null
              const selectedAdmin = availableAdmins.find(admin => admin.id === selectedAdminId)

              return selectedAdmin
                ? `${selectedAdmin.firstName} ${selectedAdmin.lastName} (${selectedAdmin.email})`
                : null
            }}
            onSelectionChange={keys => {
              const selectedKey = Array.from(keys)[0] as string

              setSelectedAdminId(selectedKey)
            }}
          >
            {availableAdmins.map(admin => (
              <SelectItem key={admin.id} className="text-gray-900 dark:text-white">
                {admin.firstName} {admin.lastName} ({admin.email})
              </SelectItem>
            ))}
          </Select>
          {availableAdmins.length === 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
              No other active tenant admins available for transfer.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            To proceed, type <span className="font-semibold">TRANSFER</span> below:
          </label>
          <Input
            placeholder="Type TRANSFER to confirm"
            value={confirmationText}
            classNames={{
              input: 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white',
              inputWrapper: 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600',
            }}
            onValueChange={setConfirmationText}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button disabled={isLoading} variant="light" onPress={onClose}>
          Cancel
        </Button>
        <Button
          color="warning"
          isDisabled={!isFormValid || availableAdmins.length === 0}
          isLoading={isLoading}
          onPress={handleTransfer}
        >
          Transfer Admin
        </Button>
      </div>
    </div>
  )
}
